import { ColorEnum } from '@/constants/color'
import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import DeliveryCarIcon from '@/features/ui/icons/components/DeliveryCarIcon'
import MenuIcon from '@/features/ui/icons/components/MenuIcon'
import TakeawayIcon from '@/features/ui/icons/components/TakeawayIcon'
import { FC, useEffect, useMemo } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated'

interface DeliveryModeSwitcherProps {
    deliveryTypeCategories: DeliveryTypeCategory[]
    onChange: (value: DeliveryTypeCategory) => void
    value: DeliveryTypeCategory
}

const iconByDeliveryTypeCategory = {
    [DeliveryTypeCategory.Takeaway]: TakeawayIcon,
    [DeliveryTypeCategory.EatIn]: MenuIcon,
    [DeliveryTypeCategory.Delivery]: DeliveryCarIcon,
} as const

const iconHorizontalOffsetByDeliveryTypeCategory = {
    [DeliveryTypeCategory.Takeaway]: 0,
    [DeliveryTypeCategory.EatIn]: -1,
    [DeliveryTypeCategory.Delivery]: 1,
} as const

const SWITCHER_HEIGHT = 40
const SWITCHER_PADDING = 4
const SWITCHER_OPTION_SIZE = 36
const SWITCHER_ICON_SIZE = 24

const DeliveryModeSwitcher: FC<DeliveryModeSwitcherProps> = ({
    deliveryTypeCategories,
    value,
    onChange,
}) => {
    const activeIndex = useMemo(
        () => Math.max(deliveryTypeCategories.indexOf(value), 0),
        [deliveryTypeCategories, value]
    )
    const highlightX = useSharedValue(activeIndex * SWITCHER_OPTION_SIZE)

    useEffect(() => {
        highlightX.value = withTiming(activeIndex * SWITCHER_OPTION_SIZE, {
            duration: 140,
            easing: Easing.out(Easing.cubic),
        })
    }, [activeIndex, highlightX])

    const highlightStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: highlightX.value }],
    }))

    return (
        <View style={styles.container}>
            <Animated.View style={[styles.highlight, highlightStyle]} />
            {deliveryTypeCategories.map((mode) => {
                const Icon = iconByDeliveryTypeCategory[mode]
                const isActive = value === mode
                const translateX = iconHorizontalOffsetByDeliveryTypeCategory[mode]

                return (
                    <Pressable key={mode} onPress={() => onChange(mode)} style={styles.option}>
                        <View style={[styles.iconWrapper, { transform: [{ translateX }] }]}>
                            <Icon
                                fill={isActive ? ColorEnum.WHITE : ColorEnum.GRAY_600}
                                height={SWITCHER_ICON_SIZE}
                                width={SWITCHER_ICON_SIZE}
                            />
                        </View>
                    </Pressable>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        backgroundColor: ColorEnum.Violet150,
        borderRadius: 8,
        flexDirection: 'row',
        height: SWITCHER_HEIGHT,
        paddingHorizontal: SWITCHER_PADDING,
        position: 'relative',
    },
    highlight: {
        backgroundColor: ColorEnum.BRICK_600,
        borderRadius: 6,
        height: SWITCHER_OPTION_SIZE,
        left: SWITCHER_PADDING,
        position: 'absolute',
        top: (SWITCHER_HEIGHT - SWITCHER_OPTION_SIZE) / 2,
        width: SWITCHER_OPTION_SIZE,
    },
    option: {
        alignItems: 'center',
        height: SWITCHER_OPTION_SIZE,
        justifyContent: 'center',
        width: SWITCHER_OPTION_SIZE,
        zIndex: 1,
    },
    iconWrapper: {
        alignItems: 'center',
        height: SWITCHER_ICON_SIZE,
        justifyContent: 'center',
        width: SWITCHER_ICON_SIZE,
    },
})

export default DeliveryModeSwitcher
